import { Table, Badge, Group, ActionIcon, Tooltip, Text, Menu, rem, Alert } from '@mantine/core'
import {
  IconTrash,
  IconEdit,
  IconDots,
  IconInfoCircle,
  IconAlertCircle,
  IconKey,
} from '@tabler/icons-react'
import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { secretsApi } from '@/api'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import type { Secret } from '@/types'
import { UpdateSecretModal } from './UpdateSecretModal'
import { useDisclosure } from '@mantine/hooks'

interface SecretTableProps {
  secrets: Secret[]
  loading: boolean
  refetch: () => void
}

export function SecretTable({ secrets, loading, refetch }: SecretTableProps) {
  const [selectedSecret, setSelectedSecret] = useState<Secret | null>(null)
  const [updateOpened, { open: openUpdate, close: closeUpdate }] = useDisclosure(false)

  const deleteMutation = useMutation({
    mutationFn: (id: string) => secretsApi.delete(id),
    onSuccess: () => {
      notifications.show({
        title: 'Secret deleted',
        message: 'Secret has been successfully deleted',
        color: 'green',
      })
      refetch()
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete secret',
        color: 'red',
      })
    },
  })

  const handleDelete = (secret: Secret) => {
    modals.openConfirmModal({
      title: 'Delete Secret',
      children: (
        <div>
          <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
            This action cannot be undone. Services using this secret may fail after deletion.
          </Alert>
          <Text size="sm">
            Are you sure you want to delete secret <strong>{secret.Spec.Name}</strong>?
          </Text>
        </div>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteMutation.mutate(secret.ID),
    })
  }

  const handleUpdate = (secret: Secret) => {
    setSelectedSecret(secret)
    openUpdate()
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString()
  }

  if (loading) {
    return <Text>Loading secrets...</Text>
  }

  if (secrets.length === 0) {
    return (
      <Alert icon={<IconInfoCircle size={16} />} title="No secrets found" color="blue">
        Create a secret to securely store sensitive data like passwords, API keys, and certificates.
      </Alert>
    )
  }

  return (
    <>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>ID</Table.Th>
            <Table.Th>Version</Table.Th>
            <Table.Th>Labels</Table.Th>
            <Table.Th>Created</Table.Th>
            <Table.Th>Updated</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {secrets.map((secret) => (
            <Table.Tr key={secret.ID}>
              <Table.Td>
                <Group gap="xs">
                  <IconKey size={16} />
                  <Text fw={500}>{secret.Spec.Name}</Text>
                </Group>
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed" ff="monospace">
                  {secret.ID.substring(0, 12)}...
                </Text>
              </Table.Td>
              <Table.Td>
                <Badge size="sm" variant="light">
                  v{secret.Version.Index}
                </Badge>
              </Table.Td>
              <Table.Td>
                {secret.Spec.Labels && Object.keys(secret.Spec.Labels).length > 0 ? (
                  <Group gap="xs">
                    {Object.entries(secret.Spec.Labels).map(([key, value]) => (
                      <Tooltip key={key} label={`${key}: ${value}`}>
                        <Badge size="sm" variant="dot">
                          {key}
                        </Badge>
                      </Tooltip>
                    ))}
                  </Group>
                ) : (
                  <Text size="sm" c="dimmed">
                    -
                  </Text>
                )}
              </Table.Td>
              <Table.Td>
                <Text size="sm">{formatDate(secret.CreatedAt)}</Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm">{formatDate(secret.UpdatedAt)}</Text>
              </Table.Td>
              <Table.Td>
                <Menu shadow="md" width={200}>
                  <Menu.Target>
                    <ActionIcon variant="subtle" color="gray">
                      <IconDots style={{ width: rem(16), height: rem(16) }} />
                    </ActionIcon>
                  </Menu.Target>

                  <Menu.Dropdown>
                    <Menu.Label>Actions</Menu.Label>
                    <Menu.Item
                      leftSection={<IconEdit style={{ width: rem(14), height: rem(14) }} />}
                      onClick={() => handleUpdate(secret)}
                    >
                      Update Secret
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                      color="red"
                      leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                      onClick={() => handleDelete(secret)}
                    >
                      Delete Secret
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {selectedSecret && (
        <UpdateSecretModal
          opened={updateOpened}
          onClose={closeUpdate}
          secret={selectedSecret}
          onSuccess={() => {
            closeUpdate()
            refetch()
          }}
        />
      )}
    </>
  )
}
